/**
 * RTWO Asset & Token Engine — Asset Event Publisher Service
 */

import type {
  ASSEventHandler,
  AssetId,
  EventId,
  RTWOAssetEvent,
  RTWOAssetEventType,
} from '../domain-models';
import type { AssetEventPublishResult, IAssetEventPublisherService } from '../service-interfaces';
import type { ASSRepositories } from '../repositories/ass-repositories';
import type { EventBusAdapterRegistry } from '../registry/adapter-registries';
import { ASSTInternalEventTypes } from '../events/ass-event-bus';
import { BaseASSService, type ASSServiceContext } from './base-ass-service';

export class AssetEventPublisherService
  extends BaseASSService
  implements IAssetEventPublisherService
{
  readonly serviceId = 'ass-event-publisher-001';
  readonly serviceName = 'Asset Event Publisher Service';
  readonly description =
    'Asset event publisher — placeholder, Event Bus not connected. No fake asset events.';
  readonly integrations = ['Core Engine', 'Event Bus', 'Indexer Engine', 'RPC Platform'];
  readonly requiresLedgerConnection = true;
  readonly requiresTokenRuntime = false;

  private readonly handlers = new Set<ASSEventHandler>();

  constructor(
    context: ASSServiceContext,
    private readonly repositories: ASSRepositories,
    private readonly eventBusRegistry: EventBusAdapterRegistry
  ) {
    super(context);
  }

  async initialize(): Promise<void> {
    this.status = 'waiting_for_ledger_connection';
  }

  async publishEvent(
    assetId: AssetId,
    eventType: RTWOAssetEventType,
    payloadReference: string,
    transactionReference: string
  ): Promise<AssetEventPublishResult> {
    if (!this.eventBusRegistry.isConfigured()) {
      return {
        success: false,
        eventId: null,
        error:
          'Asset event publishing requires real Event Bus connection — Event Bus Not Connected',
        isPlaceholder: true,
        source: 'Asset & Token Engine — Event Bus Not Connected',
      };
    }

    const adapterResult = await this.eventBusRegistry
      .getActiveAdapter()
      .publishAssetEvent(assetId, eventType, payloadReference, transactionReference);

    if (adapterResult?.event) {
      this.repositories.events.save(adapterResult.event);
      this.context.eventBus.emit(ASSTInternalEventTypes.ASSET_EVENT_PUBLISHED, {
        eventId: adapterResult.event.eventId,
        assetId,
      });
    }

    return {
      success: false,
      eventId: null,
      error: 'Asset event publishing requires real Event Bus connection — Event Bus Not Connected',
      isPlaceholder: true,
      source: 'Asset & Token Engine — Event Bus Not Connected',
    };
  }

  getAllEvents(): RTWOAssetEvent[] {
    if (!this.eventBusRegistry.isConfigured()) return [];
    return this.repositories.events.findAll();
  }

  getEventsByAssetId(assetId: AssetId): RTWOAssetEvent[] {
    if (!this.eventBusRegistry.isConfigured()) return [];
    return this.repositories.events.findByAssetId(assetId);
  }

  getEvent(eventId: EventId): RTWOAssetEvent | null {
    if (!this.eventBusRegistry.isConfigured()) return null;
    return this.repositories.events.findById(eventId);
  }

  getEventCount(): 0 {
    return 0;
  }

  subscribe(handler: ASSEventHandler): void {
    this.handlers.add(handler);
  }

  unsubscribe(handler: ASSEventHandler): void {
    this.handlers.delete(handler);
  }
}
